import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  SignOut, Phone, Buildings, ShieldStar, ListChecks, IdentificationCard,
  Plus, Trash, CaretRight, X, Broom, PencilSimple, Car, Motorcycle, Check,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const RELATIONS = ["SELF", "SPOUSE", "PARENT", "CHILD", "TENANT", "OTHER"];
const VEHICLE_TYPES = [
  { code: "TWO_WHEELER", label: "Two-wheeler", icon: Motorcycle },
  { code: "FOUR_WHEELER", label: "Four-wheeler", icon: Car },
];
const vehMeta = (code) => VEHICLE_TYPES.find((v) => v.code === code) || VEHICLE_TYPES[0];

export default function Account() {
  const { user, updateUser, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const isStaff = ["MANAGER", "CHAIRPERSON", "ADMIN"].includes(user?.role);
  const isResident = user?.role === "OWNER" || user?.role === "TENANT";
  const showMasters = isStaff || user?.role === "SECURITY";
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const vehicles = user?.vehicles || [];

  async function loadMembers() {
    if (!isResident) return;
    const { data } = await api.get("/api/flat/members"); setMembers(data.items);
  }
  useEffect(() => { loadMembers(); /* eslint-disable-next-line */ }, []);

  async function doLogout() { await logout(); navigate("/login", { replace: true }); }
  async function removeMember(id) { await api.delete(`/api/flat/members/${id}`); loadMembers(); }

  // Management = masters only (Tasks / Daily Report / directories / rosters now live on the dashboard).
  const masterLinks = [
    { to: "/masters/staff/SECURITY", label: "Security Roster", icon: ShieldStar, sub: "Add personnel & shifts" },
    { to: "/masters/staff/HOUSEKEEPING", label: "Housekeeping Roster", icon: Broom, sub: "Add housekeeping staff" },
    { to: "/masters/checklist", label: "Daily Checklist", icon: ListChecks, sub: "Manage checklist items" },
  ];

  return (
    <div className="space-y-5 font-sans" data-testid="account-screen">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Account</p>
        <h1 className="font-display text-3xl font-extrabold text-white">You</h1>
      </div>

      {/* Identity card */}
      <div className="card relative overflow-hidden p-6">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-neon-pink/15 blur-3xl" />
        {isResident && (
          <button onClick={() => setEditOpen(true)} data-testid="edit-profile-btn"
            className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[11px] font-bold text-white/80 hover:bg-white/10">
            <PencilSimple size={13} weight="bold" /> Edit
          </button>
        )}
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-neon-cyan to-neon-lime font-display text-2xl font-extrabold text-black shadow-glow-cyan">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0">
            <p className="truncate font-display text-xl font-bold text-white">{user?.name}</p>
            <p className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/70">{user?.role}</p>
          </div>
        </div>
        <dl className="mt-5 space-y-2 text-sm">
          {user?.phone && <Row icon={Phone} label="Mobile" value={`+91 ${user.phone}`} />}
          <Row icon={IdentificationCard} label="Login code" value={user?.loginCode} mono />
        </dl>
      </div>

      {/* My Flat (merged here) */}
      {isResident && user?.flat && (
        <div className="card relative overflow-hidden p-5" data-testid="my-flat-card">
          <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-neon-cyan/20 blur-3xl" />
          <div className="relative flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-neon-cyan text-black shadow-glow-cyan"><Buildings size={26} weight="duotone" /></div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-neon-cyan">My Flat</p>
              <p className="font-mono text-3xl font-extrabold text-white">{user.flat.label}</p>
              <p className="text-[12px] text-white/55">Wing {user.flat.wing} · Floor {user.flat.floor} · Unit {user.flat.number}</p>
            </div>
          </div>

          {/* Vehicles */}
          <div className="mt-5">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Vehicles</p>
              <button onClick={() => setEditOpen(true)} data-testid="add-vehicle-btn"
                className="inline-flex items-center gap-1 rounded-full bg-neon-cyan px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-black shadow-glow-cyan">
                <Plus size={12} weight="bold" /> Add
              </button>
            </div>
            <div className="mt-2 space-y-1.5">
              {vehicles.length === 0 ? (
                <p className="text-[12px] text-white/45">No vehicles added. Tap Add to register your two- or four-wheelers.</p>
              ) : vehicles.map((v, i) => {
                const M = vehMeta(v.type);
                return (
                  <div key={i} className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={`vehicle-${i}`}>
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-neon-cyan"><M.icon size={16} weight="duotone" /></div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-[13px] font-bold text-white">{v.number}</p>
                      <p className="text-[10px] uppercase tracking-wider text-white/50">{M.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Family members */}
          <div className="mt-5">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Family members</p>
              <button onClick={() => setOpen(true)} data-testid="add-member-btn"
                className="inline-flex items-center gap-1 rounded-full bg-neon-lime px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-black shadow-glow-lime">
                <Plus size={12} weight="bold" /> Add
              </button>
            </div>
            <div className="mt-2 space-y-1.5">
              {members.length === 0 ? (
                <p className="text-[12px] text-white/45">A flat can have many people — add family members so they show up across the app.</p>
              ) : members.map((m) => (
                <div key={m.id} className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-2" data-testid={`member-${m.id}`}>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-[12px] font-bold text-white">{m.name[0]?.toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-white">{m.name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-white/50">{m.relation}{m.phone ? ` · +91 ${m.phone}` : ""}</p>
                  </div>
                  <button onClick={() => removeMember(m.id)} data-testid={`remove-member-${m.id}`}
                    className="rounded-full bg-white/5 p-1.5 text-white/55 hover:text-neon-pink"><Trash size={14} weight="bold" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Masters — staff & security */}
      {showMasters && (
        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Masters</p>
          <div className="card divide-y divide-white/5 overflow-hidden p-0">
            {masterLinks.map(({ to, label, icon: Icon, sub }) => (
              <Link key={to} to={to} data-testid={`master-${to.split("/").pop()}`} className="flex items-center gap-3 px-4 py-3.5 hover:bg-white/[0.03]">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 text-neon-cyan"><Icon size={18} weight="duotone" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-[14px] font-semibold text-white">{label}</p>
                  <p className="truncate text-[11px] text-white/50">{sub}</p>
                </div>
                <CaretRight size={14} weight="bold" className="text-white/35" />
              </Link>
            ))}
          </div>
        </div>
      )}

      <button data-testid="logout-btn" onClick={doLogout}
        className="btn w-full border border-neon-pink/30 bg-neon-pink/10 text-neon-pink hover:bg-neon-pink/15">
        <SignOut size={16} weight="bold" className="mr-2" /> {t("account.signout")}
      </button>

      <p className="text-center text-[11px] text-white/35">Society Plus · v0.1 · 2026</p>

      <AnimatePresence>
        {open && <AddMemberSheet onClose={() => setOpen(false)} onAdded={() => { setOpen(false); loadMembers(); }} />}
        {editOpen && <EditProfileSheet user={user} onClose={() => setEditOpen(false)} onSaved={(u) => { updateUser(u); setEditOpen(false); }} />}
      </AnimatePresence>
    </div>
  );
}

function Row({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={16} weight="duotone" className="text-white/50" />
      <span className="w-24 text-[11px] font-bold uppercase tracking-wider text-white/50">{label}</span>
      <span className={`flex-1 text-right text-sm font-semibold text-white ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function EditProfileSheet({ user, onClose, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [vehicles, setVehicles] = useState(() => (user?.vehicles || []).map((v) => ({ ...v })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  function addVehicle() { setVehicles((a) => [...a, { type: "TWO_WHEELER", number: "" }]); }
  function setVeh(i, patch) { setVehicles((a) => a.map((v, j) => (j === i ? { ...v, ...patch } : v))); }
  function removeVeh(i) { setVehicles((a) => a.filter((_, j) => j !== i)); }

  async function submit(e) {
    e.preventDefault();
    if (name.trim().length < 2) { setErr("Please enter your name."); return; }
    const clean = vehicles.map((v) => ({ type: v.type, number: (v.number || "").trim().toUpperCase() })).filter((v) => v.number);
    setBusy(true); setErr(null);
    try {
      const { data } = await api.patch("/api/profile", { name: name.trim(), vehicles: clean });
      onSaved(data.user);
    } catch {
      setErr("Could not save. Please try again.");
    } finally { setBusy(false); }
  }

  return (
    <motion.div className="fixed inset-0 z-50 flex items-end justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.form onSubmit={submit} initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative max-h-[88vh] w-full max-w-md overflow-y-auto rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe" data-testid="edit-profile-sheet">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">Edit profile</h3>
          <button type="button" onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="label">Your name</label>
            <input className="field" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} data-testid="profile-name" />
          </div>

          {user?.flat && (
            <div>
              <label className="label">Flat number</label>
              <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                <Buildings size={16} weight="duotone" className="text-neon-cyan" />
                <span className="font-mono text-base font-bold text-white">{user.flat.label}</span>
                <span className="ml-auto text-[11px] text-white/40">Set by office</span>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <label className="label mb-0">Vehicles</label>
              <button type="button" onClick={addVehicle} data-testid="veh-add-row"
                className="inline-flex items-center gap-1 rounded-full bg-neon-cyan px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-black shadow-glow-cyan">
                <Plus size={12} weight="bold" /> Add vehicle
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {vehicles.length === 0 && <p className="text-[12px] text-white/45">Add as many vehicles as you have — pick the type and enter the number.</p>}
              {vehicles.map((v, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`veh-row-${i}`}>
                  <select className="field !w-auto !py-2.5 text-[13px]" value={v.type} onChange={(e) => setVeh(i, { type: e.target.value })} data-testid={`veh-type-${i}`}>
                    {VEHICLE_TYPES.map((vt) => <option key={vt.code} value={vt.code} className="bg-ink-800">{vt.label}</option>)}
                  </select>
                  <input className="field !py-2.5 text-[13px] uppercase" placeholder="MH12 AB 1234" value={v.number}
                    onChange={(e) => setVeh(i, { number: e.target.value })} data-testid={`veh-number-${i}`} />
                  <button type="button" onClick={() => removeVeh(i)} data-testid={`veh-remove-${i}`}
                    className="shrink-0 rounded-full bg-white/5 p-2 text-white/50 hover:text-neon-pink"><Trash size={14} weight="bold" /></button>
                </div>
              ))}
            </div>
          </div>

          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}

          <button className="btn-primary w-full" disabled={busy} data-testid="profile-save">
            {busy ? "Saving…" : <><Check size={16} weight="bold" className="mr-1.5" /> Save changes</>}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}

function AddMemberSheet({ onClose, onAdded }) {
  const [name, setName] = useState(""); const [rel, setRel] = useState("SPOUSE"); const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/api/flat/members", { name: name.trim(), relation: rel, phone: phone || null });
      onAdded();
    } finally { setBusy(false); }
  }
  return (
    <motion.div className="fixed inset-0 z-50 flex items-end justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.form onSubmit={submit} initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-md rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe" data-testid="add-member-sheet">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-white">Add family member</h3>
          <button type="button" onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <div className="mt-4 space-y-3">
          <div><label className="label">Name</label>
            <input className="field" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} data-testid="member-name" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="label">Relation</label>
              <select className="field !py-3" value={rel} onChange={(e) => setRel(e.target.value)} data-testid="member-relation">
                {RELATIONS.map((r) => <option key={r} value={r} className="bg-ink-800">{r}</option>)}
              </select></div>
            <div><label className="label">Phone (opt)</label>
              <input className="field !py-3" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit" data-testid="member-phone" /></div>
          </div>
          <button className="btn-primary w-full" disabled={busy || !name.trim()} data-testid="member-submit">{busy ? "Adding…" : "Add member"}</button>
        </div>
      </motion.form>
    </motion.div>
  );
}
